// チャートのスマホ幅(768px 未満)判定と、幅が境界をまたいだときの通知(#1486)。
//
// 以前は ApexCharts の responsive(breakpoint: 768)に任せていたが、ApexCharts は幅を広げたときに
// 初期設定を「重ねる」だけなので、スマホ幅で足したキー(ツールバー・フォント・データラベル・
// mobile_primary_axis_only で隠した右軸と軸タイトルなど)が元に戻らなかった。
// apex_chart_controller.js はこの通知を受けてチャートを作り直し、その時点の幅の設定で描き直す。
//
// win は window(テストでは偽物を渡す)。matchMedia が無い環境では innerWidth で判定し、通知はしない。

// この幅未満をスマホ幅として扱う(ApexCharts の responsive の breakpoint と同じ意味: innerWidth < 768)
export const MOBILE_BREAKPOINT = 768
// 767px ではなく 767.98px にして、767.5px などの小数の幅でも innerWidth < 768 と同じ判定にする
export const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 0.02}px)`

const resolveWindow = (win) => {
  if (win) return win
  return typeof window !== "undefined" ? window : undefined
}

const mediaQueryList = (win) => {
  if (typeof win?.matchMedia !== "function") return null
  return win.matchMedia(MOBILE_MEDIA_QUERY) || null
}

// いまスマホ幅か
export function isMobileViewport(win) {
  const target = resolveWindow(win)
  if (!target) return false

  const query = mediaQueryList(target)
  if (query) return Boolean(query.matches)

  return typeof target.innerWidth === "number" && target.innerWidth > 0 && target.innerWidth < MOBILE_BREAKPOINT
}

// measure() の値が stableFrames フレーム続けて変わらなくなったら onStable() を呼ぶ。戻り値は待つのをやめる関数。
// 幅が 768px をまたいだ直後はサイドバーの出し入れ(margin の transition 300ms)でコンテナの幅が動いている途中なので、
// 止まってからチャートを作り直す(#1486)。maxFrames 待っても止まらなければその時点で呼ぶ。
// requestFrame / cancelFrame は requestAnimationFrame / cancelAnimationFrame(無ければ 16ms の setTimeout)
export function waitForStableSize({
  measure,
  onStable,
  stableFrames = 3,
  maxFrames = 60,
  requestFrame = defaultRequestFrame,
  cancelFrame = defaultCancelFrame
} = {}) {
  if (typeof measure !== "function" || typeof onStable !== "function") return () => {}

  let lastValue = measure()
  let unchangedFrames = 0
  let frames = 0
  let handle = null
  let active = true

  const tick = () => {
    handle = null
    if (!active) return

    frames += 1
    const value = measure()
    unchangedFrames = value === lastValue ? unchangedFrames + 1 : 0
    lastValue = value

    if (unchangedFrames >= stableFrames || frames >= maxFrames) {
      active = false
      onStable(value)
      return
    }
    handle = requestFrame(tick)
  }

  handle = requestFrame(tick)

  return () => {
    if (!active) return
    active = false
    if (handle !== null) cancelFrame(handle)
    handle = null
  }
}

function defaultRequestFrame(callback) {
  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    return window.requestAnimationFrame(callback)
  }
  return setTimeout(callback, 16)
}

function defaultCancelFrame(handle) {
  if (typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
    window.cancelAnimationFrame(handle)
    return
  }
  clearTimeout(handle)
}

// 描画したチャートの幅(ApexCharts の w.globals.svgWidth)がコンテナの幅とずれているか。
// どちらかが測れない(0 / 非表示)ときは判定しない(false)
export function isChartWidthOutOfSync({ containerWidth, chartWidth, tolerance = 1 } = {}) {
  const container = Number(containerWidth)
  const chart = Number(chartWidth)
  if (!Number.isFinite(container) || !Number.isFinite(chart) || container <= 0 || chart <= 0) return false
  return Math.abs(container - chart) > tolerance
}

// スマホ幅 ⇔ それ以上 をまたいだときに onChange(isMobile) を呼ぶ。戻り値は登録を外す関数(何度呼んでもよい)。
// MediaQueryList の change は境界をまたいだときだけ発火する(同じ側での幅の変化では呼ばれない)
export function watchMobileViewport(win, onChange) {
  if (typeof onChange !== "function") return () => {}

  const target = resolveWindow(win)
  const query = target ? mediaQueryList(target) : null
  if (!query) return () => {}

  const listener = (event) => onChange(Boolean(event?.matches ?? query.matches))

  // Safari 13 以前は addEventListener を持たず addListener だけ
  if (typeof query.addEventListener === "function") {
    query.addEventListener("change", listener)
  } else if (typeof query.addListener === "function") {
    query.addListener(listener)
  } else {
    return () => {}
  }

  let active = true
  return () => {
    if (!active) return
    active = false
    if (typeof query.removeEventListener === "function") {
      query.removeEventListener("change", listener)
    } else if (typeof query.removeListener === "function") {
      query.removeListener(listener)
    }
  }
}
